import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from './cn';

const TOOLTIP_DELAY_MS = 300;
const TOOLTIP_SKIP_DELAY_MS = 150;

const ProviderPresent = /* @__PURE__ */ createContext(false);

export interface TooltipProviderProps {
  children: ReactNode;
  /** Delay before a tooltip opens on hover. Defaults to 300ms. */
  delayDuration?: number;
}

/**
 * Shares hover timing across tooltips so moving between toolbar buttons
 * shows the next tooltip immediately. Mount once near the app root;
 * a `Tooltip` rendered outside it still works with its own provider.
 */
export const TooltipProvider = ({
  children,
  delayDuration = TOOLTIP_DELAY_MS,
}: TooltipProviderProps) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}
  >
    <ProviderPresent.Provider value={true}>{children}</ProviderPresent.Provider>
  </TooltipPrimitive.Provider>
);

export interface TooltipProps {
  /** Tooltip text. When empty, the trigger renders without a tooltip. */
  content: ReactNode;
  /** A single focusable element (it receives the trigger props and ref). */
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Controlled open state (rarely needed). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Short supplementary label for a control. Tooltips must never hold the
 * only copy of essential information: pair them with an accessible name.
 */
export const Tooltip = ({
  content,
  children,
  side = 'top',
  align = 'center',
  open,
  onOpenChange,
  className,
}: TooltipProps) => {
  const hasProvider = useContext(ProviderPresent);
  if (content === null || content === undefined || content === '') {
    return children;
  }

  const tooltip = (
    <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 max-w-xs origin-[--radix-tooltip-content-transform-origin] rounded-md px-2 py-1 text-xs font-medium leading-snug shadow-md',
            'bg-fg text-surface dark:border dark:border-border-strong dark:bg-surface-raised dark:text-fg',
            'animate-fade-in data-[state=closed]:animate-fade-out',
            className
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );

  return hasProvider ? (
    tooltip
  ) : (
    <TooltipPrimitive.Provider
      delayDuration={TOOLTIP_DELAY_MS}
      skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}
    >
      {tooltip}
    </TooltipPrimitive.Provider>
  );
};
